import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { usePageMeta } from "@/hooks/usePageMeta";
import { MessageCircle, Users, TrendingUp, Clock, BookOpen, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNoIndex } from "@/hooks/useCanonicalTag";

interface TutorConversation {
  id: string;
  user_id: string;
  category: string;
  question_text: string;
  user_answer: string;
  is_correct: boolean;
  message_count: number;
  created_at: string;
  profiles?: {
    full_name: string | null;
    email: string;
  };
}

interface CategoryMetrics {
  category: string;
  total: number;
  correct: number;
  incorrect: number;
}

export default function TutorAnalytics() {
  useNoIndex();
  usePageMeta({ title: "Analytics Tutor IA | Admin", description: "Análise das conversas do Tutor IA" });
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [conversations, setConversations] = useState<TutorConversation[]>([]);
  const [metrics, setMetrics] = useState({
    totalConversations: 0,
    totalMessages: 0,
    uniqueUsers: 0,
    avgMessagesPerConversation: 0,
  });
  const [categoryMetrics, setCategoryMetrics] = useState<CategoryMetrics[]>([]);

  useEffect(() => {
    checkAdminAndFetchData();
  }, []);

  const checkAdminAndFetchData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/login");
      return;
    }

    const { data: profile } = await supabase
      .from("profiles")
      .select("is_admin")
      .eq("id", user.id)
      .single();

    if (!profile?.is_admin) {
      navigate("/candidate/dashboard");
      return;
    }

    setIsAdmin(true);
    await fetchData();
  };

  const fetchData = async () => {
    setIsLoading(true);

    // Fetch conversations with user info
    const { data: convData, error } = await supabase
      .from("tutor_conversations")
      .select(`
        id,
        user_id,
        category,
        question_text,
        user_answer,
        is_correct,
        message_count,
        created_at
      `)
      .order("created_at", { ascending: false })
      .limit(100);

    if (error) {
      console.error("Error fetching conversations:", error);
      setIsLoading(false);
      return;
    }

    // Fetch user profiles separately (admin-only RPC to include email safely)
    const userIds = [...new Set(convData?.map(c => c.user_id) || [])];
    const { data: profilesData } = await supabase
      .rpc("admin_get_user_emails", { p_user_ids: userIds });

    const profilesMap = new Map((profilesData as any[])?.map(p => [p.id, p]) || []);

    const conversationsWithProfiles = convData?.map(c => ({
      ...c,
      profiles: profilesMap.get(c.user_id)
    })) || [];

    setConversations(conversationsWithProfiles);

    // Calculate metrics
    const totalConversations = conversationsWithProfiles.length;
    const totalMessages = conversationsWithProfiles.reduce((acc, c) => acc + (c.message_count || 0), 0);
    const uniqueUsers = new Set(conversationsWithProfiles.map(c => c.user_id)).size;
    const avgMessagesPerConversation = totalConversations > 0 
      ? Math.round((totalMessages / totalConversations) * 10) / 10 
      : 0;

    setMetrics({
      totalConversations,
      totalMessages,
      uniqueUsers,
      avgMessagesPerConversation,
    });

    // Calculate category metrics
    const categoryMap = new Map<string, CategoryMetrics>();
    conversationsWithProfiles.forEach(c => {
      const existing = categoryMap.get(c.category) || { 
        category: c.category, 
        total: 0, 
        correct: 0, 
        incorrect: 0 
      };
      existing.total++;
      if (c.is_correct) {
        existing.correct++;
      } else {
        existing.incorrect++;
      }
      categoryMap.set(c.category, existing);
    });

    const sortedCategories = Array.from(categoryMap.values())
      .sort((a, b) => b.total - a.total);
    setCategoryMetrics(sortedCategories);

    setIsLoading(false);
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      sql: "SQL",
      python: "Python",
      statistics: "Estatística",
      bi: "Business Intelligence",
      kpis: "KPIs",
      google_analytics: "Google Analytics",
      google_tag_manager: "Google Tag Manager",
      looker_studio: "Looker Studio",
      power_bi: "Power BI"
    };
    return labels[category] || category;
  };

  if (isAdmin === null) {
    return (
      <CandidateLayout>
        <div className="p-6">
          <Skeleton className="h-8 w-64 mb-6" />
          <div className="grid gap-4 md:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </CandidateLayout>
    );
  }

  return (
    <CandidateLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Analytics Tutor IA</h1>
          <p className="text-muted-foreground">Análise das conversas e uso do tutor de IA</p>
        </div>

        {/* Metrics Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Conversas</CardTitle>
              <MessageCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-bold">{metrics.totalConversations}</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Mensagens</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-bold">{metrics.totalMessages}</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Usuários Únicos</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-bold">{metrics.uniqueUsers}</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Média Msgs/Conversa</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-bold">{metrics.avgMessagesPerConversation}</div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Category Distribution */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Dúvidas por Categoria
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : categoryMetrics.length === 0 ? (
                <p className="text-muted-foreground text-sm">Nenhuma conversa ainda</p>
              ) : (
                <div className="space-y-3">
                  {categoryMetrics.map((cat) => (
                    <div key={cat.category} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div>
                        <p className="font-medium text-sm">{getCategoryLabel(cat.category)}</p>
                        <div className="flex gap-2 mt-1">
                          <span className="text-xs text-green-600">{cat.correct} ✓</span>
                          <span className="text-xs text-red-600">{cat.incorrect} ✗</span>
                        </div>
                      </div>
                      <Badge variant="secondary">{cat.total}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Conversations */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Conversas Recentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-20" />
                  ))}
                </div>
              ) : conversations.length === 0 ? (
                <p className="text-muted-foreground text-sm">Nenhuma conversa ainda</p>
              ) : (
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {conversations.map((conv) => (
                      <div 
                        key={conv.id} 
                        className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant={conv.is_correct ? "default" : "destructive"} className="text-xs">
                              {conv.is_correct ? "Acertou" : "Errou"}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {getCategoryLabel(conv.category)}
                            </Badge>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(conv.created_at), "dd/MM HH:mm", { locale: ptBR })}
                          </span>
                        </div>
                        
                        <p className="text-sm font-medium line-clamp-2 mb-2">
                          {conv.question_text}
                        </p>
                        
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>
                            {conv.profiles?.full_name || conv.profiles?.email || "Usuário"}
                          </span>
                          <span className="flex items-center gap-1">
                            {conv.message_count} mensagens
                            <ChevronRight className="h-3 w-3" />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </CandidateLayout>
  );
}
